import { type AnyId } from "../schemas/branded-id.schemas.js";
import {
  BadRequestError,
  ConflictError,
  InternalServerError,
  NotFoundError,
  formatError,
  type ErrorMessageInput,
} from "./base.error.js";

export class EntityNotFoundError extends NotFoundError {
  constructor(
    public readonly entityType: string,
    public readonly identifier: AnyId,
    code: string,
  ) {
    super(
      formatError(entityType, " with identifier '", identifier, "' not found"),
      { code },
    );
  }
}

/**
 * Thrown when an entity cannot transition to the requested state.
 * Typically mapped to HTTP 409 Conflict by controllers.
 */
export class InvalidStateTransitionError extends ConflictError {
  constructor(entityType: string, currentState: string, targetState: string) {
    super(
      `Cannot transition ${entityType} from ${currentState} to ${targetState}`,
    );
  }
}

type ValidationErrorCodeOrErrorsType = string | readonly string[];

export class ValidationError extends BadRequestError {
  public readonly errors: readonly string[] | undefined;

  constructor(
    message: ErrorMessageInput,
    codeOrErrors?: ValidationErrorCodeOrErrorsType,
  ) {
    const code = typeof codeOrErrors === "string" ? codeOrErrors : undefined;
    const errors = Array.isArray(codeOrErrors) ? codeOrErrors : undefined;

    super(message, code !== undefined ? { code } : undefined);
    this.errors = errors;
  }
}

export class IdGenerationError extends InternalServerError {
  constructor(
    public readonly minLength: number,
    public readonly maxLength: number,
  ) {
    super(`Failed to generate unique ID (lengths ${minLength}-${maxLength})`);
  }
}
